import ComplexTable, {
  ComplexTableCell,
  ComplexTableHeader,
  ComplexTableProps,
  ComplexTableRow,
  ComplexTableRowRendererProps,
  ComplexTableSortableCell,
} from '@/components/ComplexTable';
import { ReactNode, forwardRef, useCallback } from 'react';

import ActionsDropdown from '@/components/ActionsDropdown';
import { Button } from 'react-bootstrap';
import ClientDropdown from '@/components/ClientDropdown';
import { ComplexFormItem } from '@/hooks/useComplexForm';
import ImportContractProviderDropdown from '@/components/ImportContractProviderDropdown';
import LocationDropdown from '@/components/LocationDropdown';
import { ProviderLocationMapping } from '@/models/gen/graphql';
import { getProperty } from '@/utils';
import useConfirmation from '@/hooks/useConfirmation';

const COLUMN_SIZES = [
  '1.5fr', // Airline (contract)
  '1.5fr', // Client
  '1.5fr', // PU Location
  '1.5fr', // DO Location
  '4rem', // Actions
];

export type ClientMappingsTableProps = Omit<ComplexTableProps, 'columns' | 'header' | 'row'>;

export type ClientMappingMetadata = {
  id: string;
  isDirty: boolean;
  isValid: boolean;
  validity: {
    contractId: boolean;
    providerId: boolean;
    puLocationId: boolean;
    doLocationId: boolean;
  };
};
const getMetadata = (item: ProviderLocationMapping, { update }: { update: Record<string, unknown> }): ClientMappingMetadata => {
  const validity = {
    contractId: !!item?.contractId,
    providerId: !!item?.providerId,
    puLocationId: !!item?.puLocationId,
    doLocationId: !!item?.doLocationId,
  };
  return {
    id: item?.id,
    isDirty: !!update,
    isValid: !item || !Object.values(validity).includes(false),
    validity,
  };
};

const ClientMappingsTable = (props: ClientMappingsTableProps, ref): ReactNode => {
  const sorting = useCallback(
    (items: ComplexFormItem<ProviderLocationMapping>[], col: number, dir: 'asc' | 'desc'): ComplexFormItem<ProviderLocationMapping>[] => {
      if (col === undefined) return items;
      const column = [
        'data.contractId', // Airline (contract)
        'data.providerId', // Client
        'data.puLocationId', // PU Location
        'data.doLocationId', // DO Location
      ][col];
      return items.sort(
        (a: ComplexFormItem<ProviderLocationMapping>, b: ComplexFormItem<ProviderLocationMapping>): number =>
          (getProperty(column, a) < getProperty(column, b) ? -1 : getProperty(column, a) > getProperty(column, b) ? 1 : 0) *
          (dir === 'desc' ? -1 : 1)
      );
    },
    []
  );

  return (
    <ComplexTable
      {...props}
      columns={COLUMN_SIZES}
      header={ClientMappingsTableHeader}
      row={ClientMappingsTableRow}
      metadata={getMetadata}
      options={{ ...(props?.options || {}), sorting }}
      style={{ maxHeight: '60vh' }}
      ref={ref}
    />
  );
};

const ClientMappingsTableHeader = (): ReactNode => {
  return (
    <ComplexTableHeader>
      <ComplexTableSortableCell index={0}>Airline</ComplexTableSortableCell>
      <ComplexTableSortableCell index={1}>Client</ComplexTableSortableCell>
      <ComplexTableSortableCell index={2}>Pickup Location</ComplexTableSortableCell>
      <ComplexTableSortableCell index={3}>Dropoff Location</ComplexTableSortableCell>
      <ComplexTableCell className="text-center">
        <Button variant="icon" className="{cursor:normal;}">
          <i className="fa fa-ellipsis-h fs-5" />
        </Button>
      </ComplexTableCell>
    </ComplexTableHeader>
  );
};

const ClientMappingsTableRow = ({ index, item, getValue, onChange, onDelete }: ComplexTableRowRendererProps): ReactNode => {
  const { validity } = item;
  const contractId = getValue('contractId');
  const providerId = getValue('providerId');
  const puLocationId = getValue('puLocationId');
  const doLocationId = getValue('doLocationId');

  const confirmDelete = useConfirmation({
    Body: { title: 'Delete Client Mapping?', message: 'Are you sure you want to delete this client mapping?' },
  });

  const handleDelete = async (): Promise<void> => {
    try {
      await confirmDelete();
      await onDelete();
    } catch (err) {
      console.log('Delete Cancelled');
    }
  };

  return (
    <ComplexTableRow>
      <ComplexTableCell>
        <ImportContractProviderDropdown
          name="contractId"
          value={contractId}
          onChange={onChange('contractId')}
          options={{ locale: { 'Select...': 'Airline' } }}
          isValid={validity?.contractId === true}
          isInvalid={validity?.contractId === false}
        />
      </ComplexTableCell>
      <ComplexTableCell>
        <ClientDropdown
          name="providerId"
          value={providerId}
          onChange={onChange('providerId')}
          options={{ locale: { 'Select...': 'Client' } }}
          isValid={validity?.providerId === true}
          isInvalid={validity?.providerId === false}
        />
      </ComplexTableCell>
      <ComplexTableCell>
        <LocationDropdown
          name="puLocationId"
          value={puLocationId}
          onChange={onChange('puLocationId')}
          options={{ locale: { 'Select...': 'Pickup Location' }, showAirportPrefix: false }}
          isValid={validity?.puLocationId === true}
          isInvalid={validity?.puLocationId === false}
        />
      </ComplexTableCell>
      <ComplexTableCell>
        <LocationDropdown
          name="doLocationId"
          value={doLocationId}
          onChange={onChange('doLocationId')}
          options={{ locale: { 'Select...': 'Dropoff Location' }, showAirportPrefix: false }}
          isValid={validity?.doLocationId === true}
          isInvalid={validity?.doLocationId === false}
        />
      </ComplexTableCell>
      <ComplexTableCell className="text-center">
        <ActionsDropdown
          icon={<i className="fa fa-ellipsis-h fs-5" />}
          items={[{ key: index, label: 'DELETE', danger: true }]}
          className="ActionsCellDropdown text-center"
          onClick={handleDelete}
        />
      </ComplexTableCell>
    </ComplexTableRow>
  );
};

export default forwardRef(ClientMappingsTable);
